import React, { createContext, useContext, useEffect, useState } from "react";
import { useFirestore } from "reactfire";
import { Country } from "../../shared/constants/Constant";
import { useAppUser } from "../user-context/UserContext";
import { WorkshopSection } from "../workshop-context/WorkshopContext";

export interface Workshop {
  id: string;
  title: string;
  description: string;
  createdAt: Date;
  country: Country;
  orderNo: number;
  thumbnail: string;
  workshopSections: WorkshopSection[];
  points: number;
  active: boolean;
  archived: boolean;
}

export interface ArchiveSubsection {
  workshopId: string;
  workshopName: string;
  isDirectArchived?: boolean;
  subsectionName: string;
  subsectionId: string;
  country: Country;
  createdAt: Date;
  archivedAt: Date;
}
export interface IWorkshopsContext {
  country:
    | "DEFAULT"
    | "SRI_LANKA"
    | "MALAYSIA"
    | "BOSNIA"
    | "JAMAICA"
    | "INDONESIA"
    | "INDIA";
  countryError: boolean;
  changeCountry: (country: Country) => void;
  onError: () => void;
  workshops: Workshop[];
  publicWorkshops: Workshop[];
  archivedWorkshops: Workshop[];
  direactArchivedWorkshops: Workshop[];
  archivedSections: ArchiveSubsection[];
  loading: boolean;
  mainLoading: boolean;
  setLoading: (value: boolean) => void;
  setShowCopyModel: (value: boolean) => void;
  setShowModel: (value: boolean) => void;
  showCompyModel: boolean;
  showModel: boolean;
  allWorkshops: Workshop[];
}

interface ICountry {
  country:
    | "DEFAULT"
    | "SRI_LANKA"
    | "MALAYSIA"
    | "BOSNIA"
    | "JAMAICA"
    | "INDONESIA"
    | "INDIA";
  countryError: boolean;
}

const initialContext: IWorkshopsContext = {
  country: "DEFAULT",
  countryError: false,
  changeCountry: () => {},
  onError: () => {},
  loading: false,
  workshops: [],
  publicWorkshops: [],
  archivedSections: [],
  archivedWorkshops: [],
  direactArchivedWorkshops: [],
  setLoading: (value: boolean) => {},
  setShowCopyModel: (value: boolean) => {},
  setShowModel: (value: boolean) => {},
  showCompyModel: false,
  showModel: false,
  mainLoading: false,
  allWorkshops: [],
};

const initialState: ICountry = {
  country: "DEFAULT",
  countryError: false,
};

const WorkshopsContext = createContext<IWorkshopsContext>(initialContext);

interface IProps {
  children: any;
}

export const WorkshopsProvider = (props: IProps) => {
  const [countryState, setCountryState] = useState(initialState);
  const appUser = useAppUser();
  const [loadingState, setLoadingState] = useState(false);
  const [mainLoadingState, setMainLoadingState] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [publicWorkshops, setPublicWorkshops] = useState<Workshop[]>([]);
  const [allWorkshops, setAllWorkshops] = useState<Workshop[]>([]);
  const [fWorkshops, setFWorkshops] = useState<Workshop[]>([]);
  const [archivedWorkshops, setArchivedWorkshops] = useState<Workshop[]>([]);
  const [direactArchivedWorkshops, setDireactArchivedWorkshops] = useState<
    Workshop[]
  >([]);
  const [fArchivedWorkshops, setFArchivedWorkshops] = useState<Workshop[]>([]);

  const [archiveSubsections, setArchiveSubsections] = useState<
    ArchiveSubsection[]
  >([]);
  const [fArchiveSubsections, setFArchiveSubsections] = useState<
    ArchiveSubsection[]
  >([]);

  const store = useFirestore();

  useEffect(() => {
    let archivedSubsections = allWorkshops
      .map(
        (workshop) =>
          workshop.workshopSections
            ?.filter((section) => section.archived && section.isDirectArchived)
            .map((archivedSection) => {
              return {
                archivedAt: new Date(
                  (archivedSection.archivedAt as any)?.seconds * 1000 ||
                    new Date().getTime()
                ),
                country: workshop.country,
                createdAt: new Date(
                  (archivedSection.createdAt as any)?.seconds * 1000 ||
                    new Date().getTime()
                ),
                subsectionId: archivedSection.id || "",
                subsectionName: archivedSection.subSectionTitle,
                workshopId: workshop.id,
                workshopName: workshop.title,
              };
            }) || []
      )
      .reduce((prev, next) => prev.concat(next), []);
    setArchiveSubsections(archivedSubsections);
  }, [allWorkshops]);

  // const [fWorkshops, setFWorkshops] = useState<Workshop[]>([]);
  useEffect(() => {
    const selectedCountry = countryState.country !== "DEFAULT";
    if (selectedCountry) {
      setModalShow(false);
      setFWorkshops((ps) => {
        return workshops
          .filter((w) => w.country === countryState.country)
          .sort((a: Workshop, b: Workshop) => a.orderNo - b.orderNo);
      });
    } else {
      if (appUser.userRole.isAdmin) {
        setModalShow(true);
      }
    }
  }, [countryState.country, workshops, appUser.userRole.isAdmin]);

  useEffect(() => {
    const selectedCountry = countryState.country !== "DEFAULT";
    if (selectedCountry) {
      setModalShow(false);
      setFArchivedWorkshops((ps) => {
        return archivedWorkshops
          .filter((w) => w.country === countryState.country)
          .sort((a: Workshop, b: Workshop) => a.orderNo - b.orderNo);
      });
    } else {
      if (appUser.userRole.isAdmin) {
        setModalShow(true);
      }
    }
  }, [countryState.country, archivedWorkshops, appUser.userRole.isAdmin]);

  useEffect(() => {
    const selectedCountry = countryState.country !== "DEFAULT";
    if (selectedCountry) {
      setModalShow(false);
      setFArchiveSubsections((ps) => {
        return archiveSubsections.filter(
          (w) => w.country === countryState.country
        );
      });
    } else {
      if (appUser.userRole.isAdmin) {
        setModalShow(true);
      }
    }
  }, [countryState.country, archiveSubsections, appUser.userRole.isAdmin]);

  useEffect(() => {
    setPublicWorkshops([]);
    return store
      .collection("publicWorkshops")
      .where("active", "==", true)
      .onSnapshot({}, (snapshot) => {
        setLoading(false);
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            setPublicWorkshops((ps) => {
              ps.push({
                id: change.doc.id,
                title: change.doc.data().title,
                description: change.doc.data().description,
                createdAt: change.doc.data().createdAt,
                country: change.doc.data().country,
                orderNo: change.doc.data().orderNo,
                thumbnail: change.doc.data().thumbnail,
                workshopSections: [],
                points: 0,
                active: true,
                archived: false,
              });
              return Object.assign([], ps);
            });
          }
          if (change.type === "modified") {
            setWorkshops((ps) => {
              const modified = ps.map((a) => {
                if (a.id === change.doc.id) {
                  return {
                    id: change.doc.id,
                    title: change.doc.data().title,
                    description: change.doc.data().description,
                    createdAt: change.doc.data().createdAt,
                    country: change.doc.data().country,
                    orderNo: change.doc.data().orderNo,
                    thumbnail: change.doc.data().thumbnail,
                  };
                } else {
                  return a;
                }
              });
              return Object.assign([], modified);
            });
          }
          if (change.type === "removed") {
            setWorkshops((ps) => {
              const removed = ps.filter((a) => a.id !== change.doc.id);
              return Object.assign([], removed);
            });
          }
        });
      });
  }, [store]);

  useEffect(() => {
    if (appUser.fireUser && !appUser.userRole.isAdmin)
      setCountryState({ country: appUser.country, countryError: false });
  }, [appUser.country, appUser.userRole.isAdmin, appUser.fireUser]);

  useEffect(() => {
    setMainLoadingState(true);
    return store.collection("workshops").onSnapshot({}, (snapshot) => {
      setMainLoadingState(false);
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          setAllWorkshops((ps) => {
            ps.push({
              id: change.doc.id,
              title: change.doc.data().title,
              description: change.doc.data().description,
              createdAt: change.doc.data().createdAt,
              country: change.doc.data().country,
              orderNo: change.doc.data().orderNo,
              thumbnail: change.doc.data().thumbnail,
              workshopSections: change.doc.data().workshopSections,
              points: change.doc.data().points,
              active: change.doc.data().active,
              archived: change.doc.data().archived,
            });
            return Object.assign([], ps);
          });
        }
        if (change.type === "modified") {
          setAllWorkshops((ps) => {
            const modified = ps.map((a) => {
              if (a.id === change.doc.id) {
                return {
                  id: change.doc.id,
                  title: change.doc.data().title,
                  description: change.doc.data().description,
                  createdAt: change.doc.data().createdAt,
                  country: change.doc.data().country,
                  orderNo: change.doc.data().orderNo,
                  thumbnail: change.doc.data().thumbnail,
                  workshopSections: change.doc.data().workshopSections,
                  points: change.doc.data().points,
                  active: change.doc.data().active,
                  archived: change.doc.data().archived,
                };
              } else {
                return a;
              }
            });
            return Object.assign([], modified);
          });
        }
        if (change.type === "removed") {
          setAllWorkshops((ps) => {
            const removed = ps.filter((a) => a.id !== change.doc.id);
            return Object.assign([], removed);
          });
        }
      });
    });
  }, [store]);

  useEffect(() => {
    setWorkshops([
      ...allWorkshops
        .filter((w) => w.active && !w.archived)
        .filter((w) => !w.workshopSections || w.workshopSections?.length === 0),
      ...allWorkshops
        .filter((w) => w.active)
        .map((w) => ({
          ...w,
          workshopSections: w.workshopSections?.filter((s) => !s.archived),
        }))
        .filter((w) => w.workshopSections?.length !== 0),
    ]);
  }, [allWorkshops]);

  useEffect(() => {
    setArchivedWorkshops([
      ...allWorkshops
        .map((w) => ({
          ...w,
          workshopSections: w.workshopSections?.filter(
            (s) => s.archived && !s.isDirectArchived
          ),
        }))
        .filter((w) => w.workshopSections && w.workshopSections?.length !== 0),
    ]);
  }, [allWorkshops]);

  useEffect(() => {
    setDireactArchivedWorkshops([
      ...allWorkshops
        .map((w) => ({
          ...w,
          workshopSections: w.workshopSections?.filter(
            (s) => s.archived && s.isDirectArchived
          ),
        }))
        .filter((w) => w.workshopSections && w.workshopSections?.length !== 0),
    ]);
  }, [allWorkshops]);

  const changeCountry = (
    country:
      | "DEFAULT"
      | "SRI_LANKA"
      | "MALAYSIA"
      | "BOSNIA"
      | "JAMAICA"
      | "INDIA"
      | "INDONESIA"
  ) => {
    setCountryState((ps) => ({ ...ps, country, countryError: false }));
  };
  const onError = () => {
    setCountryState({ ...countryState, countryError: true });
  };

  const setLoading = (value: boolean) => {
    setLoadingState(value);
  };

  const showCopyModel = (value: boolean) => {
    setShowCopyDialog(value);
  };
  const showModel = (value: boolean) => {
    setModalShow(value);
  };
  return (
    <WorkshopsContext.Provider
      value={{
        allWorkshops,
        country: countryState.country,
        publicWorkshops,
        countryError: countryState.countryError,
        loading: loadingState,
        archivedSections: fArchiveSubsections,
        setLoading,
        workshops: fWorkshops,
        archivedWorkshops: fArchivedWorkshops,
        direactArchivedWorkshops,
        changeCountry,
        onError,
        setShowCopyModel: showCopyModel,
        setShowModel: showModel,
        showModel: modalShow,
        showCompyModel: showCopyDialog,
        mainLoading: mainLoadingState,
      }}
    >
      {props.children}
    </WorkshopsContext.Provider>
  );
};

export const useWorkshops = (): IWorkshopsContext =>
  useContext(WorkshopsContext);
